'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Shampoo & Conditioner: 87% Contain Polymers"
      author="MicroPlastics Editorial Team"
      date="May 1, 2025"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in shampoo and conditioner, synthetic polymers and silicones"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Most mainstream shampoos and conditioners contain <strong>dimethicone
        (silicone), polyquaternium polymers, acrylates copolymer, and PEG
        derivatives</strong>. Beat the Microbead estimates ~87% of personal-care
        products contain plastic polymers. While most rinse down the drain
        (contributing to waterway microplastic pollution), they also coat hair
        and skin during contact. Switching to <strong>shampoo bars (Ethique,
        HiBAR, Lush, Plaine Products), polymer-free liquid shampoos (Acure,
        Dr. Bronner&apos;s, John Masters Organics), and silicone-free
        conditioners</strong> eliminates both personal exposure and waterway
        pollution.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>~87% of personal-care products contain plastic polymers per Beat the Microbead audits.</>,
          <>Common polymers in shampoo: <strong>dimethicone, dimethiconol, polyquaternium, acrylates copolymer, carbomer, PEG-(any number)</strong>.</>,
          <>Most polymers rinse into wastewater, they are major contributors to municipal microplastic pollution.</>,
          <>Hair conditioning silicones (dimethicone) coat hair and skin during use; small absorption occurs through scalp.</>,
          <>Safer alternatives: shampoo bars (Ethique, HiBAR, Lush), polymer-free liquid (Acure, Dr. Bronner&apos;s, John Masters), silicone-free conditioners.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why shampoo matters
      </h2>
      <p>
        You wash your hair 2-7 times a week, every week, for life. Each
        wash deposits and rinses synthetic polymers in and through your
        scalp&apos;s pores, then sends most of the polymer down the drain
        into waterways. Two distinct concerns:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Personal exposure.</strong> Scalp skin absorbs small amounts of polymer chemistry during use; hair retains silicones between washes.</li>
        <li><strong>Environmental pollution.</strong> Rinsed polymers reach wastewater treatment plants, which weren&apos;t designed to remove dissolved polymer chemistry. Most pass through to rivers.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The plastic ingredients to scan for
      </h2>

      <BlogComparisonTable
        caption="Synthetic polymers in shampoo and conditioner"
        columns={[
          { key: 'ingredient', label: 'Ingredient name' },
          { key: 'function', label: 'Function' },
          { key: 'product', label: 'Common in' },
        ]}
        rows={[
          { ingredient: 'Dimethicone / Dimethiconol', function: 'Silicone hair coating; &ldquo;slip&rdquo;', product: 'Most conditioners; smoothing shampoos' },
          { ingredient: 'Cyclomethicone / Cyclopentasiloxane', function: 'Volatile silicone; light feel', product: 'Leave-in conditioners, anti-frizz' },
          { ingredient: 'Polyquaternium-(any number)', function: 'Conditioning, anti-static', product: 'Most major-brand shampoos' },
          { ingredient: 'Acrylates copolymer / crosspolymer', function: 'Hold, film-former', product: 'Volumising and styling shampoos' },
          { ingredient: 'Carbomer', function: 'Thickener', product: 'Gel shampoos and clear conditioners' },
          { ingredient: 'PEG-(any number)', function: 'Surfactant, emulsifier', product: 'Most shampoos' },
          { ingredient: 'Polyvinyl pyrrolidone (PVP)', function: 'Film former, hold', product: 'Styling-friendly products' },
          { ingredient: 'Polysorbate-20, -80', function: 'Emulsifier', product: 'Many products' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The silicone debate
      </h2>
      <p>
        Dimethicone is the dominant hair-conditioning silicone. It coats hair
        strands for a smooth, slippery feel and reduces breakage. It&apos;s
        not technically a microplastic (it&apos;s a polymerised silicone) but it&apos;s a synthetic polymer that doesn&apos;t biodegrade in
        wastewater systems and accumulates in sediment and waterways. It also
        builds up on hair over time, requiring sulfate-based shampoos to
        remove (a cleansing cycle that strips natural oils).
      </p>
      <p>
        Silicone-free conditioners use natural butters (shea, mango, cocoa)
        and oils (argan, jojoba, coconut, sunflower) for conditioning.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended polymer-free brands
      </h2>

      <BlogComparisonTable
        caption="Microplastic-conscious shampoo and conditioner brands"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'format', label: 'Format' },
          { key: 'cert', label: 'Cert / Notes' },
          { key: 'price', label: 'Price' },
        ]}
        rows={[
          { brand: 'Ethique Shampoo Bars', format: 'Solid bar', cert: 'B-Corp; plastic-free packaging; polymer-free', price: '$16-20 (lasts 2-3 months)' },
          { brand: 'HiBAR Shampoo &amp; Conditioner Bars', format: 'Solid bars', cert: 'Plastic-free packaging; polymer-free; sulfate-free', price: '$15-18' },
          { brand: 'Lush Shampoo Bars', format: 'Solid bar', cert: 'Wide variety; polymer-free', price: '$13-15' },
          { brand: 'Acure Shampoo &amp; Conditioner', format: 'Liquid in recyclable plastic', cert: 'Polymer-free liquid; widely available', price: '$10-13' },
          { brand: 'Dr. Bronner\'s Sugar Soap (multi-use)', format: 'Liquid', cert: 'Multi-purpose; polymer-free; certified organic', price: '$12-16' },
          { brand: 'John Masters Organics Shampoo', format: 'Liquid', cert: 'Organic; polymer-free in most products', price: '$25-32' },
          { brand: 'Plaine Products', format: 'Liquid in aluminium refillable bottle', cert: 'Closed-loop refill; polymer-free', price: '$28 initial; $24 refill' },
          { brand: 'Conventional drugstore (Pantene, Herbal Essences, Garnier)', format: 'Liquid in plastic', cert: 'Contains polymers, silicones, PEG', price: '$5-8' },
          { brand: 'Salon brands (Olaplex, Redken, Pureology)', format: 'Liquid in plastic', cert: 'Contains polymers and silicones for hair-conditioning effects', price: '$20-45' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical hair-routine changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Try a shampoo bar.</strong> Ethique, HiBAR, and Lush are widely available. One bar = 2-3 bottles of liquid shampoo with no plastic packaging.</li>
        <li><strong>Use a wide-tooth wooden comb</strong> instead of plastic brushes (which shed microplastic fibres into hair).</li>
        <li><strong>Wash less often.</strong> Daily washing strips natural oils and increases need for conditioning silicones. Most hair types benefit from 2-3 washes per week.</li>
        <li><strong>Air-dry or low-heat dry.</strong> Reduces need for heat-protection sprays (often polymer-heavy).</li>
        <li><strong>Skip leave-in styling products</strong> with polyquaternium, acrylates, or PVP, these polymers stay on hair until next wash.</li>
        <li><strong>Conditioner in glass containers</strong> when available, or refill at zero-waste shops.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics
        </Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in toothpaste
        </Link>, and{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best polymer-free toothpaste
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does shampoo contain microplastics?',
            answer:
              'Most mainstream shampoos contain synthetic polymers including dimethicone (silicone), polyquaternium, acrylates copolymer, carbomer, and PEG derivatives. Beat the Microbead estimates ~87% of personal-care products contain plastic polymers.',
          },
          {
            question: 'Is silicone in conditioner bad?',
            answer:
              'Dimethicone (the most common conditioning silicone) coats hair for a smooth feel and rinses down the drain into wastewater. It is not biodegradable in real-world conditions and accumulates in sediment. Silicone-free conditioners use natural butters and oils instead.',
          },
          {
            question: 'Are shampoo bars better than liquid shampoo?',
            answer:
              'Shampoo bars (Ethique, HiBAR, Lush) are typically polymer-free, use no plastic packaging, and last 2-3 times longer than liquid bottles. The main learning curve is adjusting hair to bar shampoo over 2-4 weeks; most users report softer, healthier hair afterward.',
          },
          {
            question: 'What is the safest shampoo brand for microplastics?',
            answer:
              'Ethique, HiBAR, Lush (shampoo bars); Acure, Dr. Bronner\'s, John Masters Organics, Plaine Products (liquid). Mainstream drugstore brands (Pantene, Herbal Essences, Garnier) and most salon brands (Olaplex, Redken, Pureology) contain polymers and silicones.',
          },
          {
            question: 'How can I tell if my shampoo has microplastics?',
            answer:
              'Scan the ingredient list for: dimethicone, dimethiconol, cyclomethicone, polyquaternium-(any number), acrylates copolymer, carbomer, PEG-(any number), PVP, polysorbate-20/-80. The Beat the Microbead app scans products against a public microplastic ingredient database.',
          },
          {
            question: 'Do polymer-free shampoos work as well?',
            answer:
              'They work differently. Without silicones, hair may feel less "slippery" initially but is genuinely cleaner. After 2-4 weeks of adjustment, most users report healthier, more manageable hair. The "silicone build-up cycle" of needing harsher shampoos to remove silicones is broken.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Beat the Microbead / Plastic Soup Foundation',
            year: 2024,
            title: 'Plastic in personal care products: database',
            publisher: 'Plastic Soup Foundation',
            url: 'https://www.beatthemicrobead.org/',
          },
          {
            authors: 'European Chemicals Agency (ECHA)',
            year: 2023,
            title: 'Restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'Lassen C, et al.',
            year: 2015,
            title: 'Microplastics: Occurrence, effects and sources of releases to the environment in Denmark',
            publisher: 'Danish Environmental Protection Agency',
            url: 'https://www2.mst.dk/Udgiv/publications/2015/10/978-87-93352-80-3.pdf',
          },
          {
            authors: 'Environmental Working Group (EWG)',
            year: 2024,
            title: 'EWG\'s Skin Deep Cosmetics Database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/skindeep/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
